import React, { useState, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Plus, Camera, MapPin, RotateCcw, CheckCircle2, XCircle, Clock, Eye, Image, FileText, Loader2, Pencil, Ban, Trash2, AlertTriangle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { EmpExportButton } from './EmpExportButton';
import { WorkflowQueueCard } from './WorkflowQueueCard';
import { useEmpExecucoes, useEmpExecucaoMutations, useEmpEvidencias, getEvidenciaUrl, EmpExecucao } from '@/hooks/useEmpExecucao';
import { useExecucaoTimeline, EVENT_TYPE_LABELS, EVENT_TYPE_COLORS } from '@/hooks/useExecucaoTimeline';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useEpcQtdFlag } from '@/hooks/useEpcQtdFlag';

interface EmpExecucaoViewProps {
  contratoId: string;
  contratoNome: string;
  canEdit: boolean;
}

const STATUS_COLORS: Record<string, { variant: 'default' | 'destructive' | 'outline' | 'secondary'; icon: React.ElementType; label: string }> = {
  em_apontamento: { variant: 'outline', icon: Clock, label: 'Em Apontamento' },
  aguardando_qualidade: { variant: 'secondary', icon: Clock, label: 'Aguardando Qualidade' },
  aguardando_planejamento: { variant: 'secondary', icon: Clock, label: 'Aguardando Planejamento' },
  disponivel_para_medicao: { variant: 'default', icon: CheckCircle2, label: 'Disponível p/ Medição' },
  vinculada_a_boletim: { variant: 'default', icon: CheckCircle2, label: 'Vinculada a Boletim' },
  rejeitado: { variant: 'destructive', icon: XCircle, label: 'Rejeitado' },
  cancelado: { variant: 'destructive', icon: Ban, label: 'Cancelado' },
};

const formatDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(new Date(d + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR }); }
  catch { return d; }
};

export const EmpExecucaoView: React.FC<EmpExecucaoViewProps> = ({ contratoId, contratoNome, canEdit }) => {
  const { role } = useAuth();
  const { execucoes, isLoading } = useEmpExecucoes(contratoId);
  const { createExecucao, transicionarExecucao, uploadEvidencia } = useEmpExecucaoMutations(contratoId);
  const { enabled: epcQtdEnabled } = useEpcQtdFlag();

  // ═══ Guard: block execution registration unless planilha AND cronograma are vigente ═══
  const { data: prontidaoStatus, isLoading: prontidaoLoading } = useQuery({
    queryKey: ['emp-prontidao-execucao', contratoId],
    queryFn: async () => {
      const [planilhaRes, cronogramaRes] = await Promise.all([
        supabase
          .from('emp_planilha_versoes')
          .select('id, status')
          .eq('contrato_id', contratoId)
          .eq('status', 'vigente')
          .limit(1)
          .maybeSingle(),
        supabase
          .from('emp_cronograma_versoes')
          .select('id, status')
          .eq('contrato_id', contratoId)
          .eq('status', 'vigente')
          .limit(1)
          .maybeSingle(),
      ]);
      return {
        planilhaVigente: !!planilhaRes.data,
        cronogramaVigente: !!cronogramaRes.data,
      };
    },
    enabled: !!contratoId,
    staleTime: 30_000,
  });

  const execucaoBloqueada = !prontidaoLoading && prontidaoStatus && (!prontidaoStatus.planilhaVigente || !prontidaoStatus.cronogramaVigente);
  const motivosBloqueio: string[] = [];
  if (prontidaoStatus && !prontidaoStatus.planilhaVigente) motivosBloqueio.push('Planilha Orçamentária não ativada');
  if (prontidaoStatus && !prontidaoStatus.cronogramaVigente) motivosBloqueio.push('Cronograma não ativado');

  // EPC-QTD progress data (conditional — only fetched when flag ON)
  const { data: epcProgressData } = useQuery({
    queryKey: ['emp-progresso-epc-execucao', contratoId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('emp_vw_progresso_epc_qtd')
        .select('atividade_id, progresso_fisico, ev, quantidade_takeoff, quantidade_executada, valor_ponderado, planilha_item_id')
        .eq('contrato_id', contratoId);
      if (error) {
        console.debug('[EVM:EXEC] EPC progress query failed, hiding column:', error.message);
        return null;
      }
      return data;
    },
    enabled: epcQtdEnabled && !!contratoId,
    staleTime: 3 * 60 * 1000,
  });

  const epcProgressMap = useMemo(() => {
    if (!epcProgressData) return null;
    const map = new Map<string, typeof epcProgressData[0]>();
    for (const row of epcProgressData) {
      if (row.planilha_item_id) map.set(row.planilha_item_id, row);
    }
    return map.size > 0 ? map : null;
  }, [epcProgressData]);

  const showEpcColumn = !!epcProgressMap;

  const [registerOpen, setRegisterOpen] = useState(false);
  const [detailExecucao, setDetailExecucao] = useState<EmpExecucao | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tipoFilter, setTipoFilter] = useState<string>('all');

  // B4a: Revision modal state
  const [revisionExec, setRevisionExec] = useState<EmpExecucao | null>(null);
  const [revisionQtd, setRevisionQtd] = useState('');
  const [revisionJustificativa, setRevisionJustificativa] = useState('');

  // B4b: Cancel modal state
  const [cancelExec, setCancelExec] = useState<EmpExecucao | null>(null);
  const [cancelJustificativa, setCancelJustificativa] = useState('');
  const [cancelConfirmed, setCancelConfirmed] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    planilha_item_id: '',
    frente_id: '',
    data_execucao: new Date().toISOString().slice(0, 10),
    quantidade_executada: '',
    unidade: '',
    observacoes: '',
    localizacao_descricao: '',
    tipo: 'execucao' as 'execucao' | 'retrabalho',
    execucao_original_id: '',
  });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Frentes de obra for selection
  const { data: frentes = [], isLoading: frentesLoading } = useQuery({
    queryKey: ['emp-frentes-select', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_frentes_trabalho')
        .select('id, nome, municipio_ibge, municipios:municipios!emp_frentes_trabalho_municipio_ibge_fkey(nome, uf)')
        .eq('contrato_id', contratoId)
        .eq('status', 'ativa')
        .order('nome');
      if (error) throw error;
      return (data || []).map((f: any) => ({
        id: f.id,
        nome: f.nome,
        municipio_ibge: f.municipio_ibge,
        municipio_nome: f.municipios?.nome || null,
        municipio_uf: f.municipios?.uf || null,
      }));
    },
    enabled: !!contratoId,
  });

  const selectedFrente = frentes.find(f => f.id === formData.frente_id);

  // Fetch contract type to determine EPC leaf-filtering
  const { data: contratoMeta } = useQuery({
    queryKey: ['emp-contrato-meta-exec', contratoId],
    queryFn: async () => {
      const { data } = await supabase
        .from('emp_contratos')
        .select('tipo_contrato_operacional')
        .eq('id', contratoId)
        .maybeSingle();
      return data as { tipo_contrato_operacional: string } | null;
    },
    enabled: !!contratoId,
    staleTime: 60_000,
  });

  const isEpcContract = contratoMeta?.tipo_contrato_operacional === 'preco_global';

  // Planilha items for selection — fetch from vigente or latest rascunho version
  // For EPC contracts: structural leaf filtering via emp_planilha_grupos (no heuristics)
  const { data: planilhaItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['emp-planilha-items-select', contratoId, isEpcContract],
    queryFn: async () => {
      const { data: versao } = await supabase
        .from('emp_planilha_versoes')
        .select('id, status')
        .eq('contrato_id', contratoId)
        .in('status', ['vigente', 'rascunho'])
        .order('numero_versao', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!versao) return [];
      const { data: items, error } = await supabase
        .from('emp_planilha_itens')
        .select('id, codigo_item, descricao, unidade, quantidade_contratada, valor_total, metodo_medicao')
        .eq('versao_id', versao.id)
        .eq('status', 'ativo')
        .order('ordem');
      if (error) return [];
      const allItems = items || [];

      // EPC: structural leaf filtering — exclude items whose codigo_item exists in emp_planilha_grupos
      if (isEpcContract) {
        const { data: grupos } = await supabase
          .from('emp_planilha_grupos')
          .select('codigo')
          .eq('versao_id', versao.id);
        const grupoCodes = new Set((grupos || []).map((g: any) => g.codigo));

        // If no groups exist (legacy contract without hierarchy), show all items (permissive fallback)
        if (grupoCodes.size === 0) return allItems;

        // Filter: leaf = item whose codigo_item is NOT a group code
        return allItems.filter(item => !grupoCodes.has(item.codigo_item));
      }
      return allItems;
    },
    enabled: !!contratoId,
    staleTime: 30_000,
  });

  // Legacy EPC banner: detect EPC contract without hierarchy
  const { data: epcHasHierarchy } = useQuery({
    queryKey: ['emp-epc-has-hierarchy', contratoId, isEpcContract],
    queryFn: async () => {
      const { data: versao } = await supabase
        .from('emp_planilha_versoes')
        .select('id')
        .eq('contrato_id', contratoId)
        .eq('status', 'vigente')
        .limit(1)
        .maybeSingle();
      if (!versao) return true; // No vigente version — no banner needed
      const { count } = await supabase
        .from('emp_planilha_grupos')
        .select('id', { count: 'exact', head: true })
        .eq('versao_id', versao.id);
      return (count || 0) > 0;
    },
    enabled: !!contratoId && isEpcContract,
    staleTime: 60_000,
  });

  const showLegacyEpcBanner = isEpcContract && epcHasHierarchy === false;

  const filtered = execucoes.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (tipoFilter !== 'all' && e.tipo !== tipoFilter) return false;
    return true;
  });

  const counts = {
    total: execucoes.length,
    pendente: execucoes.filter(e => ['em_apontamento', 'aguardando_qualidade', 'aguardando_planejamento'].includes(e.status)).length,
    aprovado: execucoes.filter(e => ['disponivel_para_medicao', 'vinculada_a_boletim'].includes(e.status)).length,
    rejeitado: execucoes.filter(e => e.status === 'rejeitado').length,
  };

  const resetForm = () => {
    setFormData({
      planilha_item_id: '',
      frente_id: '',
      data_execucao: new Date().toISOString().slice(0, 10),
      quantidade_executada: '',
      unidade: '',
      observacoes: '',
      localizacao_descricao: '',
      tipo: 'execucao',
      execucao_original_id: '',
    });
    setPendingFiles([]);
  };

  const handleItemSelect = (itemId: string) => {
    const item = planilhaItems.find((i: any) => i.id === itemId);
    setFormData(prev => ({
      ...prev,
      planilha_item_id: itemId,
      unidade: item?.unidade || prev.unidade,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.quantidade_executada || !formData.unidade || !formData.data_execucao || !formData.frente_id) return;

    const result = await createExecucao.mutateAsync({
      contrato_id: contratoId,
      planilha_item_id: formData.planilha_item_id || null,
      frente_id: formData.frente_id,
      data_execucao: formData.data_execucao,
      quantidade_executada: parseFloat(formData.quantidade_executada),
      unidade: formData.unidade,
      tipo: formData.tipo,
      execucao_original_id: formData.tipo === 'retrabalho' && formData.execucao_original_id ? formData.execucao_original_id : null,
      observacoes: formData.observacoes || null,
      localizacao_descricao: formData.localizacao_descricao || null,
    });

    // Upload pending photos with EXIF validation
    if (result && pendingFiles.length > 0) {
      for (const file of pendingFiles) {
        await uploadEvidencia.mutateAsync({
          execucaoId: result.id,
          file,
        });
      }
    }

    resetForm();
    setRegisterOpen(false);
  };

  const handleRetrabalho = (exec: EmpExecucao) => {
    setFormData({
      planilha_item_id: exec.planilha_item_id || '',
      frente_id: exec.frente_id || '',
      data_execucao: new Date().toISOString().slice(0, 10),
      quantidade_executada: String(exec.quantidade_executada),
      unidade: exec.unidade,
      observacoes: `Retrabalho ref. execução ${formatDate(exec.data_execucao)}`,
      localizacao_descricao: exec.localizacao_descricao || '',
      tipo: 'retrabalho',
      execucao_original_id: exec.id,
    });
    setRegisterOpen(true);
  };

  const isGC = role === 'admin' || role === 'coadmin';

  return (
    <div className="space-y-4">
      {/* Bloqueio de execução banner */}
      {execucaoBloqueada && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-destructive">Registro de execução bloqueado</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Conclua e ative todas as planilhas obrigatórias do contrato antes de registrar execuções:
                </p>
                <ul className="text-sm text-muted-foreground mt-1 list-disc pl-4">
                  {motivosBloqueio.map(m => <li key={m}>{m}</li>)}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Legacy EPC banner — contract without hierarchy */}
      {showLegacyEpcBanner && (
        <Card className="border-yellow-500/50 bg-yellow-50/50 dark:bg-yellow-900/10">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-500">Hierarquia EPC não configurada</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Este contrato EPC não possui estrutura hierárquica. Recomenda-se reimportar a planilha para habilitar validações avançadas e filtragem de itens folha.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: counts.total, color: 'text-foreground' },
          { label: 'Pendentes', value: counts.pendente, color: 'text-orange-500' },
          { label: 'Aprovados', value: counts.aprovado, color: 'text-green-600' },
          { label: 'Rejeitados', value: counts.rejeitado, color: 'text-destructive' },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">{k.label}</p>
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Workflow Queue Indicators */}
      <WorkflowQueueCard contratoId={contratoId} />

      {/* Filters + Actions */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="em_apontamento">Em Apontamento</SelectItem>
            <SelectItem value="aguardando_qualidade">Ag. Qualidade</SelectItem>
            <SelectItem value="aguardando_planejamento">Ag. Planejamento</SelectItem>
            <SelectItem value="disponivel_para_medicao">Disp. Medição</SelectItem>
            <SelectItem value="vinculada_a_boletim">Vinculada</SelectItem>
            <SelectItem value="cancelado">Cancelados</SelectItem>
            <SelectItem value="rejeitado">Rejeitados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="execucao">Execução</SelectItem>
            <SelectItem value="retrabalho">Retrabalho</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <EmpExportButton
          getData={() => execucoes.map(e => ({
            'Data': e.data_execucao,
            'Item': e.item_codigo || e.planilha_item_id || '',
            'Tipo': e.tipo,
            'Quantidade': e.quantidade_executada,
            'Unidade': e.unidade,
            'Status': e.status,
            'Local': e.localizacao_descricao || '',
            'Observações': e.observacoes || '',
          }))}
          filename={`execucoes_${contratoNome}`}
          printTitle={`Execuções — ${contratoNome}`}
        />
        {canEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button 
                  onClick={() => { resetForm(); setRegisterOpen(true); }}
                  disabled={!!execucaoBloqueada}
                >
                  <Plus className="h-4 w-4 mr-1" /> Registrar Execução
                </Button>
              </span>
            </TooltipTrigger>
            {execucaoBloqueada && (
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="font-medium">Execução não permitida</p>
                <p className="text-xs mt-1">Conclua e ative todas as planilhas obrigatórias antes de registrar execuções:</p>
                <ul className="text-xs mt-1 list-disc pl-3">
                  {motivosBloqueio.map(m => <li key={m}>{m}</li>)}
                </ul>
              </TooltipContent>
            )}
          </Tooltip>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg font-medium">Nenhuma execução registrada</p>
              <p className="text-sm mt-1">Registre a primeira execução de campo.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
               <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead>Qualidade</TableHead>
                  {showEpcColumn && <TableHead>Progresso TK</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(exec => {
                  const sc = STATUS_COLORS[exec.status] || STATUS_COLORS.em_apontamento;
                  const StatusIcon = sc.icon;
                  return (
                    <TableRow key={exec.id}>
                      <TableCell className="text-sm">{formatDate(exec.data_execucao)}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">
                        {exec.item_codigo || exec.planilha_item_id?.slice(0, 8) || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={exec.tipo === 'retrabalho' ? 'destructive' : 'outline'} className="text-xs">
                          {exec.tipo === 'retrabalho' ? '↺ Retrabalho' : 'Execução'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{exec.quantidade_executada}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{exec.unidade}</TableCell>
                      <TableCell>
                        {exec.qualidade_status ? (
                          <Badge variant={exec.qualidade_status === 'aprovada' ? 'default' : exec.qualidade_status === 'rejeitada' ? 'destructive' : 'outline'} className="text-xs">
                            {exec.qualidade_status === 'aprovada' ? '✓ Qualidade' : exec.qualidade_status === 'rejeitada' ? '✗ Qualidade' : '⏳ Qualidade'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">—</Badge>
                        )}
                      </TableCell>
                      {showEpcColumn && (
                        <TableCell>
                          {(() => {
                            const epcRow = exec.planilha_item_id ? epcProgressMap?.get(exec.planilha_item_id) : null;
                            if (!epcRow) return <span className="text-muted-foreground text-xs">—</span>;
                            const pct = Math.min(100, (epcRow.progresso_fisico || 0) * 100);
                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="min-w-[80px]">
                                    <div className="flex items-center justify-between text-xs mb-0.5">
                                      <span className={
                                        pct >= 80 ? 'text-primary' :
                                        pct >= 50 ? 'text-warning' : 'text-destructive'
                                      }>{pct.toFixed(1)}%</span>
                                    </div>
                                    <Progress value={pct} className="h-1.5" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="text-xs space-y-0.5">
                                    <p>EV: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(epcRow.ev || 0)}</p>
                                    <p>Take-off: {epcRow.quantidade_executada?.toLocaleString('pt-BR')} / {epcRow.quantidade_takeoff?.toLocaleString('pt-BR')}</p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant={sc.variant} className="text-xs flex items-center gap-1 w-fit">
                          <StatusIcon className="h-3 w-3" />
                          {sc.label || exec.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setDetailExecucao(exec)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* B4a: Revisar e Reenviar (em_apontamento with previous rejection) */}
                          {isGC && exec.status === 'em_apontamento' && exec.qualidade_status === 'rejeitada' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-amber-600"
                                  onClick={() => { setRevisionExec(exec); setRevisionQtd(String(exec.quantidade_executada)); setRevisionJustificativa(''); }}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Revisar e Reenviar</TooltipContent>
                            </Tooltip>
                          )}
                          {/* Apontador: submit for quality review */}
                          {isGC && exec.status === 'em_apontamento' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-blue-600"
                                  onClick={() => transicionarExecucao.mutate({ id: exec.id, novoStatus: 'aguardando_qualidade' })}>
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Enviar para Qualidade</TooltipContent>
                            </Tooltip>
                          )}
                          {/* B4b: Cancelar apontamento */}
                          {isGC && exec.status === 'em_apontamento' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive"
                                  onClick={() => { setCancelExec(exec); setCancelJustificativa(''); setCancelConfirmed(false); }}>
                                  <Ban className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Cancelar Apontamento</TooltipContent>
                            </Tooltip>
                          )}
                          {/* Quality gate: approve/reject */}
                          {isGC && exec.status === 'aguardando_qualidade' && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-green-600"
                                    onClick={() => transicionarExecucao.mutate({ id: exec.id, novoStatus: 'aguardando_planejamento', observacoes: 'Qualidade aprovada' })}>
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Aprovar Qualidade</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-orange-500"
                                    onClick={() => transicionarExecucao.mutate({ id: exec.id, novoStatus: 'em_apontamento', motivoRejeicao: 'Rejeitado pela Qualidade' })}>
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Reprovar Qualidade</TooltipContent>
                              </Tooltip>
                            </>
                          )}
                          {/* Planning validation */}
                          {isGC && exec.status === 'aguardando_planejamento' && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-green-600"
                                    onClick={() => transicionarExecucao.mutate({ id: exec.id, novoStatus: 'disponivel_para_medicao', observacoes: 'Planejamento aprovado' })}>
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Aprovar Planejamento</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-destructive"
                                    onClick={() => transicionarExecucao.mutate({ id: exec.id, novoStatus: 'aguardando_qualidade', motivoRejeicao: 'Devolvido pelo Planejamento' })}>
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Devolver ao Qualidade</TooltipContent>
                              </Tooltip>
                            </>
                          )}
                          {exec.status === 'rejeitado' && canEdit && (
                            <Button variant="ghost" size="icon" className="text-orange-500" onClick={() => handleRetrabalho(exec)}>
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Register Dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{formData.tipo === 'retrabalho' ? '↺ Registrar Retrabalho' : 'Registrar Execução'}</DialogTitle>
            <DialogDescription>Preencha os dados da execução em campo.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {formData.tipo === 'retrabalho' && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800 dark:bg-orange-950/30 dark:border-orange-800 dark:text-orange-300">
                ↺ Retrabalho vinculado a execução anterior
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {/* Frente de Obra — obrigatória */}
              <div className="col-span-2">
                <Label>Frente de Obra *</Label>
                {frentesLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : frentes.length === 0 ? (
                  <div className="text-sm text-muted-foreground bg-muted/50 rounded p-3">
                    Nenhuma frente de obra cadastrada. Crie uma frente antes de registrar execuções.
                  </div>
                ) : (
                  <Select value={formData.frente_id} onValueChange={v => setFormData(p => ({ ...p, frente_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecionar frente..." /></SelectTrigger>
                    <SelectContent>
                      {frentes.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.nome} {f.municipio_nome ? `— ${f.municipio_nome} – ${f.municipio_uf}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedFrente?.municipio_nome && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    <span>Município: {selectedFrente.municipio_nome} – {selectedFrente.municipio_uf}</span>
                  </div>
                )}
              </div>

              <div className="col-span-2">
                <Label>Item da Planilha</Label>
                {itemsLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : planilhaItems.length === 0 ? (
                  <div className="text-sm text-muted-foreground bg-muted/50 rounded p-3">
                    Nenhum item disponível. Verifique se a Planilha de Preços (PPU) foi importada e ativada.
                  </div>
                ) : (
                  <Select value={formData.planilha_item_id} onValueChange={handleItemSelect}>
                    <SelectTrigger><SelectValue placeholder="Selecionar item..." /></SelectTrigger>
                    <SelectContent>
                      {planilhaItems.map((item: any) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.codigo_item} — {item.descricao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label>Data de Execução *</Label>
                <Input type="date" value={formData.data_execucao} onChange={e => setFormData(p => ({ ...p, data_execucao: e.target.value }))} />
              </div>
              <div>
                <Label>Unidade *</Label>
                <Input value={formData.unidade} onChange={e => setFormData(p => ({ ...p, unidade: e.target.value }))} placeholder="m², m³, un..." />
              </div>

              <div className="col-span-2">
                <Label>Quantidade Executada *</Label>
                <Input type="number" step="0.01" min="0" value={formData.quantidade_executada} onChange={e => setFormData(p => ({ ...p, quantidade_executada: e.target.value }))} placeholder="0.00" />
              </div>

              <div className="col-span-2">
                <Label>Localização / Descrição</Label>
                <Input value={formData.localizacao_descricao} onChange={e => setFormData(p => ({ ...p, localizacao_descricao: e.target.value }))} placeholder="Ex: Bloco A, 3º pavimento" />
              </div>

              <div className="col-span-2">
                <Label>Observações</Label>
                <Textarea value={formData.observacoes} onChange={e => setFormData(p => ({ ...p, observacoes: e.target.value }))} rows={2} placeholder="Notas adicionais..." />
              </div>

              {/* Photo upload */}
              <div className="col-span-2">
                <Label className="flex items-center gap-1"><Camera className="h-4 w-4" /> Evidências Fotográficas</Label>
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    className="hidden"
                    onChange={e => {
                      const files = Array.from(e.target.files || []);
                      setPendingFiles(prev => [...prev, ...files]);
                      e.target.value = '';
                    }}
                  />
                  <div className="flex flex-wrap gap-2 mb-2">
                    {pendingFiles.map((f, i) => (
                      <div key={i} className="relative group">
                        <img src={URL.createObjectURL(f)} alt={f.name} className="h-16 w-16 object-cover rounded border" />
                        <button
                          className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-4 h-4 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                        >×</button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Camera className="h-4 w-4 mr-1" /> Adicionar Fotos
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={createExecucao.isPending || !formData.quantidade_executada || !formData.unidade || !formData.frente_id}
            >
              {createExecucao.isPending ? 'Salvando...' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / Validation Dialog */}
      {detailExecucao && (
        <ExecucaoDetailDialog
          execucao={detailExecucao}
          contratoId={contratoId}
          isGC={isGC}
          onClose={() => setDetailExecucao(null)}
          onTransicionar={(novoStatus, observacoes, motivoRejeicao) => {
            transicionarExecucao.mutate({ id: detailExecucao.id, novoStatus, observacoes, motivoRejeicao });
            setDetailExecucao(null);
          }}
          onRetrabalho={() => {
            handleRetrabalho(detailExecucao);
            setDetailExecucao(null);
          }}
        />
      )}

      {/* B4a: Revision Modal */}
      <Dialog open={!!revisionExec} onOpenChange={(v) => { if (!v) setRevisionExec(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>✏️ Revisar e Reenviar</DialogTitle>
            <DialogDescription>
              Corrija a quantidade executada e reenvie para aprovação de Qualidade.
            </DialogDescription>
          </DialogHeader>
          {revisionExec && (
            <div className="space-y-4">
              {revisionExec.motivo_rejeicao && (
                <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-sm">
                  <p className="font-medium text-destructive text-xs mb-1">Motivo da rejeição anterior:</p>
                  <p className="text-muted-foreground">{revisionExec.motivo_rejeicao}</p>
                </div>
              )}
              <div>
                <Label>Quantidade atual</Label>
                <p className="text-lg font-bold text-muted-foreground line-through">{revisionExec.quantidade_executada} {revisionExec.unidade}</p>
              </div>
              <div>
                <Label>Nova quantidade *</Label>
                <Input type="number" step="0.01" min="0.01" value={revisionQtd} onChange={e => setRevisionQtd(e.target.value)} />
              </div>
              <div>
                <Label>Justificativa * (mín. 20 caracteres)</Label>
                <Textarea value={revisionJustificativa} onChange={e => setRevisionJustificativa(e.target.value)}
                  placeholder="Explique a correção da quantidade..." minLength={20} />
                <p className="text-xs text-muted-foreground mt-1">{revisionJustificativa.length}/20 caracteres</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionExec(null)}>Cancelar</Button>
            <Button
              disabled={!revisionQtd || parseFloat(revisionQtd) <= 0 || revisionJustificativa.trim().length < 20 || transicionarExecucao.isPending}
              onClick={() => {
                if (!revisionExec) return;
                transicionarExecucao.mutate({
                  id: revisionExec.id,
                  novoStatus: 'aguardando_qualidade',
                  observacoes: revisionJustificativa.trim(),
                  motivoRejeicao: JSON.stringify({ nova_quantidade: parseFloat(revisionQtd) }),
                }, { onSuccess: () => setRevisionExec(null) });
              }}>
              <Pencil className="h-4 w-4 mr-1" /> Revisar e Reenviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* B4b: Cancel Modal */}
      <Dialog open={!!cancelExec} onOpenChange={(v) => { if (!v) setCancelExec(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🚫 Cancelar Apontamento</DialogTitle>
            <DialogDescription>
              Esta ação é irreversível. O apontamento será cancelado permanentemente.
            </DialogDescription>
          </DialogHeader>
          {cancelExec && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-sm">
                <p className="text-muted-foreground">
                  <strong>Item:</strong> {cancelExec.item_codigo || cancelExec.planilha_item_id?.slice(0, 8) || '—'} · 
                  <strong> Qtd:</strong> {cancelExec.quantidade_executada} {cancelExec.unidade} · 
                  <strong> Data:</strong> {formatDate(cancelExec.data_execucao)}
                </p>
              </div>
              <div>
                <Label>Motivo do cancelamento * (mín. 20 caracteres)</Label>
                <Textarea value={cancelJustificativa} onChange={e => setCancelJustificativa(e.target.value)}
                  placeholder="Explique por que este apontamento deve ser cancelado..." minLength={20} />
                <p className="text-xs text-muted-foreground mt-1">{cancelJustificativa.length}/20 caracteres</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cancelConfirmed} onChange={e => setCancelConfirmed(e.target.checked)}
                  className="rounded border-destructive" />
                <span className="text-sm text-destructive font-medium">Entendo que esta ação é irreversível</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelExec(null)}>Voltar</Button>
            <Button variant="destructive"
              disabled={cancelJustificativa.trim().length < 20 || !cancelConfirmed || transicionarExecucao.isPending}
              onClick={() => {
                if (!cancelExec) return;
                transicionarExecucao.mutate({
                  id: cancelExec.id,
                  novoStatus: 'cancelado',
                  observacoes: cancelJustificativa.trim(),
                }, { onSuccess: () => setCancelExec(null) });
              }}>
              <Ban className="h-4 w-4 mr-1" /> Cancelar Apontamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Detail / Validation Dialog ---
interface ExecucaoDetailDialogProps {
  execucao: EmpExecucao;
  contratoId: string;
  isGC: boolean;
  onClose: () => void;
  onTransicionar: (novoStatus: string, observacoes?: string, motivoRejeicao?: string) => void;
  onRetrabalho: () => void;
}

const ExecucaoDetailDialog: React.FC<ExecucaoDetailDialogProps> = ({ execucao, contratoId, isGC, onClose, onTransicionar, onRetrabalho }) => {
  const { evidencias, isLoading: evidLoading } = useEmpEvidencias(execucao.id);
  const { uploadEvidencia, excluirEvidencia } = useEmpExecucaoMutations(contratoId);
  const { data: timelineEvents = [] } = useExecucaoTimeline(execucao.id);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [qaFiles, setQaFiles] = useState<File[]>([]);
  const [planFiles, setPlanFiles] = useState<File[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; nome: string } | null>(null);
  const [deleteJustificativa, setDeleteJustificativa] = useState('');
  const [uploading, setUploading] = useState(false);
  const qaFileRef = React.useRef<HTMLInputElement>(null);
  const planFileRef = React.useRef<HTMLInputElement>(null);

  const handleApproveWithAttachments = async (
    novoStatus: string,
    obs: string,
    files: File[],
    categoria: string,
    clearFiles: () => void
  ) => {
    setUploading(true);
    try {
      // Upload attachments first
      for (const file of files) {
        await uploadEvidencia.mutateAsync({
          execucaoId: execucao.id,
          file,
          tipo: 'documento',
          categoria,
          descricao: `${categoria === 'relatorio_inspecao_qualidade' ? 'Relatório de Inspeção' : 'Evidência de Planejamento'} — ${file.name}`,
        });
      }
      // Then transition
      onTransicionar(novoStatus, obs);
      clearFiles();
    } catch (err: any) {
      console.error('Erro ao anexar documentos:', err);
    } finally {
      setUploading(false);
    }
  };

  // Load signed URLs for evidence images
  React.useEffect(() => {
    evidencias.forEach(async (ev) => {
      if (!imageUrls[ev.id]) {
        const url = await getEvidenciaUrl(ev.storage_path);
        if (url) setImageUrls(prev => ({ ...prev, [ev.id]: url }));
      }
    });
  }, [evidencias]);

  const sc = STATUS_COLORS[execucao.status] || STATUS_COLORS.em_apontamento;
  const StatusIcon = sc.icon;

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Detalhes da Execução
            <Badge variant={sc.variant} className="flex items-center gap-1">
              <StatusIcon className="h-3 w-3" /> {execucao.status}
            </Badge>
            {execucao.tipo === 'retrabalho' && (
              <Badge variant="destructive">↺ Retrabalho</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Data:</span>
              <p className="font-medium">{formatDate(execucao.data_execucao)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Quantidade:</span>
              <p className="font-medium">{execucao.quantidade_executada} {execucao.unidade}</p>
            </div>
            {execucao.localizacao_descricao && (
              <div className="col-span-2">
                <span className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Localização:</span>
                <p className="font-medium">{execucao.localizacao_descricao}</p>
              </div>
            )}
            {execucao.observacoes && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Observações:</span>
                <p className="font-medium">{execucao.observacoes}</p>
              </div>
            )}
            {execucao.motivo_rejeicao && (
              <div className="col-span-2 p-2 bg-destructive/10 border border-destructive/30 rounded">
                <span className="text-destructive text-xs font-medium">Motivo da rejeição:</span>
                <p className="text-sm">{execucao.motivo_rejeicao}</p>
              </div>
            )}
          </div>

          {/* Evidence photos */}
          <div>
            <h4 className="text-sm font-medium flex items-center gap-1 mb-2">
              <Image className="h-4 w-4" /> Evidências ({evidencias.length})
            </h4>
            {evidLoading ? (
              <div className="flex gap-2">
                {[1, 2].map(i => <Skeleton key={i} className="h-32 w-32 rounded" />)}
              </div>
            ) : evidencias.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma evidência fotográfica.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {evidencias.map(ev => (
                  <div key={ev.id} className="space-y-1 relative group/ev">
                    {imageUrls[ev.id] ? (
                      <img src={imageUrls[ev.id]} alt={ev.nome_arquivo} className="w-full h-32 object-cover rounded border" />
                    ) : (
                      <Skeleton className="w-full h-32 rounded" />
                    )}
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground truncate flex-1">{ev.nome_arquivo}</p>
                      {isGC && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget({ id: ev.id, nome: ev.nome_arquivo })}
                          className="opacity-0 group-hover/ev:opacity-100 transition-opacity p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                          title="Excluir evidência"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {/* EXIF info */}
                    {(ev.exif_latitude || ev.exif_dispositivo || ev.exif_data_hora) && (
                      <div className="text-xs space-y-0.5 p-1.5 bg-muted/50 rounded">
                        {ev.exif_data_hora && (
                          <p className="flex items-center gap-1"><Clock className="h-3 w-3" /> {format(new Date(ev.exif_data_hora), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
                        )}
                        {ev.exif_latitude && ev.exif_longitude && (
                          <p className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.exif_latitude.toFixed(6)}, {ev.exif_longitude.toFixed(6)}</p>
                        )}
                        {ev.exif_dispositivo && (
                          <p className="flex items-center gap-1"><Camera className="h-3 w-3" /> {ev.exif_dispositivo}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Timeline per execution */}
          {timelineEvents.length > 0 && (
            <div>
              <h4 className="text-sm font-medium flex items-center gap-1 mb-2">
                <Clock className="h-4 w-4" /> Histórico ({timelineEvents.length})
              </h4>
              <div className="space-y-0">
                {timelineEvents.slice(0, 10).map((evt, idx) => {
                  const dotColor = EVENT_TYPE_COLORS[evt.tipo_evento] || 'bg-muted-foreground';
                  const label = EVENT_TYPE_LABELS[evt.tipo_evento] || evt.tipo_evento;
                  const isLast = idx === Math.min(timelineEvents.length - 1, 9);
                  return (
                    <div key={evt.id} className="flex gap-2.5">
                      <div className="flex flex-col items-center">
                        <div className={`w-2.5 h-2.5 rounded-full ${dotColor} flex-shrink-0 mt-1.5`} />
                        {!isLast && <div className="w-px flex-1 bg-border/40 mt-0.5" />}
                      </div>
                      <div className="flex-1 pb-2.5 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="text-xs">{label}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(evt.created_at), "dd/MM HH:mm", { locale: ptBR })}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{evt.descricao}</p>
                        {evt.usuario_nome && (
                          <p className="text-xs text-muted-foreground/70 mt-0.5">por {evt.usuario_nome}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                {timelineEvents.length > 10 && (
                  <p className="text-xs text-muted-foreground text-center">+{timelineEvents.length - 10} eventos anteriores</p>
                )}
              </div>
            </div>
          )}

          {/* Workflow actions based on current status */}
          {isGC && execucao.status === 'em_apontamento' && (
            <Card className="border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Ações do Workflow</CardTitle>
              </CardHeader>
              <CardContent>
                <Button className="w-full" onClick={() => onTransicionar('aguardando_qualidade', 'Enviado para qualidade')}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Enviar para Qualidade
                </Button>
              </CardContent>
            </Card>
          )}

          {isGC && execucao.status === 'aguardando_qualidade' && (
            <Card className="border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Validação de Qualidade</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* PDF attachment area */}
                <div>
                  <Label className="text-xs text-muted-foreground">Relatórios de Inspeção (PDF) — opcional</Label>
                  <input ref={qaFileRef} type="file" accept=".pdf" multiple className="hidden"
                    onChange={e => { setQaFiles(prev => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                  <div className="flex flex-wrap gap-2 mt-1">
                    {qaFiles.map((f, i) => (
                      <Badge key={i} variant="secondary" className="text-xs gap-1">
                        {f.name}
                        <button onClick={() => setQaFiles(prev => prev.filter((_, j) => j !== i))} className="ml-1 text-destructive hover:text-destructive/80">×</button>
                      </Badge>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => qaFileRef.current?.click()}>
                    <FileText className="h-3 w-3 mr-1" /> Anexar PDF
                  </Button>
                </div>
                <Separator />
                {showReject ? (
                  <div className="space-y-2">
                    <Label>Motivo da rejeição *</Label>
                    <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Descreva o motivo..." rows={2} />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setShowReject(false)}>Cancelar</Button>
                      <Button variant="destructive" size="sm" disabled={!rejectReason.trim()} onClick={() => onTransicionar('em_apontamento', undefined, rejectReason)}>
                        Confirmar Rejeição
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button className="flex-1" disabled={uploading}
                      onClick={() => handleApproveWithAttachments('aguardando_planejamento', 'Qualidade aprovada', qaFiles, 'relatorio_inspecao_qualidade', () => setQaFiles([]))}>
                      {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                      Aprovar Qualidade{qaFiles.length > 0 ? ` (${qaFiles.length} anexo${qaFiles.length > 1 ? 's' : ''})` : ''}
                    </Button>
                    <Button variant="destructive" className="flex-1" onClick={() => setShowReject(true)}>
                      <XCircle className="h-4 w-4 mr-1" /> Reprovar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {isGC && execucao.status === 'aguardando_planejamento' && (
            <Card className="border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Validação de Planejamento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* PDF attachment area */}
                <div>
                  <Label className="text-xs text-muted-foreground">Evidências de Planejamento (PDF) — opcional</Label>
                  <input ref={planFileRef} type="file" accept=".pdf" multiple className="hidden"
                    onChange={e => { setPlanFiles(prev => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                  <div className="flex flex-wrap gap-2 mt-1">
                    {planFiles.map((f, i) => (
                      <Badge key={i} variant="secondary" className="text-xs gap-1">
                        {f.name}
                        <button onClick={() => setPlanFiles(prev => prev.filter((_, j) => j !== i))} className="ml-1 text-destructive hover:text-destructive/80">×</button>
                      </Badge>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => planFileRef.current?.click()}>
                    <FileText className="h-3 w-3 mr-1" /> Anexar PDF
                  </Button>
                </div>
                <Separator />
                {showReject ? (
                  <div className="space-y-2">
                    <Label>Motivo da devolução *</Label>
                    <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Descreva o motivo..." rows={2} />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setShowReject(false)}>Cancelar</Button>
                      <Button variant="destructive" size="sm" disabled={!rejectReason.trim()} onClick={() => onTransicionar('aguardando_qualidade', undefined, rejectReason)}>
                        Devolver ao Qualidade
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button className="flex-1" disabled={uploading}
                      onClick={() => handleApproveWithAttachments('disponivel_para_medicao', 'Planejamento aprovado', planFiles, 'evidencia_planejamento', () => setPlanFiles([]))}>
                      {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                      Aprovar Planejamento{planFiles.length > 0 ? ` (${planFiles.length} anexo${planFiles.length > 1 ? 's' : ''})` : ''}
                    </Button>
                    <Button variant="destructive" className="flex-1" onClick={() => setShowReject(true)}>
                      <XCircle className="h-4 w-4 mr-1" /> Devolver
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Rework button for rejected */}
          {execucao.status === 'rejeitado' && (
            <Button variant="outline" className="w-full" onClick={onRetrabalho}>
              <RotateCcw className="h-4 w-4 mr-1" /> Registrar Retrabalho
            </Button>
          )}
        </div>

        {/* Delete evidence confirmation dialog */}
        {deleteTarget && (
          <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteJustificativa(''); } }}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <Trash2 className="h-4 w-4" /> Excluir evidência
                </DialogTitle>
                <DialogDescription>
                  A exclusão de "{deleteTarget.nome}" será registrada na auditoria com rastreabilidade permanente.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label>Justificativa <span className="text-destructive">*</span></Label>
                <Textarea
                  value={deleteJustificativa}
                  onChange={e => setDeleteJustificativa(e.target.value)}
                  placeholder="Descreva o motivo da exclusão (mín. 10 caracteres)..."
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">{deleteJustificativa.trim().length}/10 caracteres mínimos</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteJustificativa(''); }}>Cancelar</Button>
                <Button
                  variant="destructive"
                  disabled={deleteJustificativa.trim().length < 10 || excluirEvidencia.isPending}
                  onClick={() => {
                    excluirEvidencia.mutate({
                      evidenciaId: deleteTarget.id,
                      execucaoId: execucao.id,
                      justificativa: deleteJustificativa,
                    }, {
                      onSuccess: () => { setDeleteTarget(null); setDeleteJustificativa(''); },
                    });
                  }}
                >
                  {excluirEvidencia.isPending ? 'Excluindo...' : 'Confirmar exclusão'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};
